import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invoiceKeys } from '@/entities/invoice'
import { apiRequest } from '@/shared/api'
import type { FileRef } from '@/shared/lib'

export interface IssueInvoiceVars {
  number: string
  amount_kopecks: number
  due_date?: string
  file: FileRef
}

// Цель счёта: ровно одно из project_id | company_id уходит в тело POST /invoices.
export type IssueInvoiceTarget = { project_id: string } | { company_id: string }

// Счёт мог лечь в контур проекта и/или компании — инвалидация плоская.
function useInvalidate() {
  const qc = useQueryClient()
  return (invoiceId?: string) => {
    void qc.invalidateQueries({ queryKey: invoiceKeys.all })
    if (invoiceId) void qc.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) })
  }
}

export function useIssueInvoice() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: IssueInvoiceVars & IssueInvoiceTarget) =>
      apiRequest({ method: 'POST', url: '/invoices', body: { ...vars } }),
    onSuccess: () => invalidate(),
  })
}

// Правка: номер, сумма, срок, файл. Срок снимается флагом — null схему не проходит.
export type EditInvoiceVars = {
  number?: string
  amount_kopecks?: number
  due_date?: string
  clear_due_date?: boolean
  file?: FileRef
}

// Оплаченный счёт заморожен: бэкенд отвечает 409, поэтому у него нет и кнопок.
export function useEditInvoice(invoiceId: string) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: EditInvoiceVars) =>
      apiRequest({ method: 'PATCH', url: `/invoices/${invoiceId}`, body: vars }),
    onSuccess: () => invalidate(invoiceId),
  })
}

// Удаление жёсткое, с каскадом: комментарии, теги, слоты документов и файл.
export function useDeleteInvoice(invoiceId: string) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: () => apiRequest({ method: 'DELETE', url: `/invoices/${invoiceId}` }),
    onSuccess: () => invalidate(invoiceId),
  })
}
